import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import API from '../api/axios';

const PDFPage = ({ pdfDoc, pageNum, totalPages, isSelected, onClick, pageSelection, orientation, scale, containerWidth }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    let renderTask = null;
    let isCancelled = false;

    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(pageNum);
        if (isCancelled) return;

        // Inherit base rotation and apply user orientation (landscape adds 90deg)
        const baseRotation = page.rotate || 0;
        const isLandscape = orientation === 'landscape';
        const targetRotation = isLandscape ? (baseRotation + 90) % 360 : baseRotation;

        // Get unscaled viewport to calculate its base dimensions at this target rotation
        const unscaledViewport = page.getViewport({ scale: 1.0, rotation: targetRotation });

        // Available width inside container, accounting for padding/borders
        const availableWidth = Math.max(120, containerWidth - 24);

        // 100% scale factor represents the full sheet size (always fits the container width)
        const scaleFactor100 = availableWidth / unscaledViewport.width;
        const viewport100 = page.getViewport({ scale: scaleFactor100, rotation: targetRotation });

        // Content viewport is scaled relative to the 100% sheet viewport
        const currentScalePercent = scale || 90;
        const scaleFactorContent = scaleFactor100 * (currentScalePercent / 100);
        const renderViewport = page.getViewport({ scale: scaleFactorContent, rotation: targetRotation });

        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        // High DPI rendering for crisp text on mobile - render at 2x device pixel ratio
        const dpr = 2; // device pixel ratio multiplier
        canvas.width = viewport100.width * dpr;
        canvas.height = viewport100.height * dpr;
        context.scale(dpr, dpr);

        // Clear canvas with solid white to represent the "white paper sheet"
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);

        // Calculate translation offset to draw content perfectly centered inside the sheet
        const dx = (viewport100.width - renderViewport.width) / 2;
        const dy = (viewport100.height - renderViewport.height) / 2;

        context.save();
        context.translate(dx, dy);

        const renderContext = {
          canvasContext: context,
          viewport: renderViewport
        };
        renderTask = page.render(renderContext);
        await renderTask.promise;
        context.restore();
      } catch (err) {
        if (err.name !== 'RenderingCancelledException') {
          console.error(`Error rendering PDF page ${pageNum}:`, err);
        }
      }
    };

    renderPage();

    return () => {
      isCancelled = true;
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdfDoc, pageNum, orientation, scale, containerWidth]);

  const isCustomMode = pageSelection === 'custom';
  const isRangeMode = pageSelection === 'range';
  const isInteractiveMode = isCustomMode || isRangeMode;
  const showUnselectedDim = isInteractiveMode && !isSelected;

  return (
    <div
      className={`pdf-page-wrapper ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
      style={{
        position: 'relative',
        margin: '0 auto 20px auto',
        cursor: isCustomMode ? 'pointer' : 'default',
        width: 'fit-content',
        borderRadius: '2px',
        overflow: 'hidden',
        border: 'none',
        boxShadow: isSelected ? '0 8px 24px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.25)',
        opacity: showUnselectedDim ? 0.35 : 1,
        filter: showUnselectedDim ? 'grayscale(60%)' : 'none',
        transition: 'all 0.25s ease',
        transform: isCustomMode && isSelected ? 'scale(1.01)' : 'none'
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', maxWidth: '100%', height: 'auto' }} />
      
      {/* Centered Page Number & Selection Pill (Mac OS Style) */}
      <div
        style={{
          position: 'absolute',
          bottom: '12px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(242, 242, 247, 0.92)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '6px 14px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
          zIndex: 2,
          pointerEvents: 'none',
          border: '0.5px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease'
        }}
      >
        {/* Blue Check circle */}
        <div
          style={{
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            background: isSelected ? '#007aff' : 'rgba(0, 0, 0, 0.1)',
            border: isSelected ? 'none' : '1.5px solid rgba(0, 0, 0, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '9.5px',
            fontWeight: '900',
            transition: 'all 0.2s ease'
          }}
        >
          {isSelected ? '✓' : ''}
        </div>
        <span
          style={{
            fontSize: '11px',
            fontWeight: '600',
            color: '#1c1c1e',
            letterSpacing: '-0.1px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}
        >
          Page {pageNum} of {totalPages}
        </span>
      </div>
    </div>
  );
};

const PDFViewer = ({ file, selectedPages = [], onPageToggle, pageSelection, orientation = 'portrait', scale = 90 }) => {
  const [pdfDoc, setPdfDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fallback, setFallback] = useState(false);
  const [containerWidth, setContainerWidth] = useState(260);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth || 260);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [loading]);

  useEffect(() => {
    const localBlob = (file?.file instanceof Blob ? file.file : null) || 
                      (file?.localFile instanceof Blob ? file.localFile : null);

    if (!file?.viewLink && !localBlob) {
      setError('No preview link available');
      setLoading(false);
      return;
    }

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        setFallback(false);

        let uint8Array;
        if (localBlob) {
          // Instant client-side preview directly from local file buffer!
          const buffer = await localBlob.arrayBuffer();
          uint8Array = new Uint8Array(buffer);
        } else {
          // Fetch the PDF file via proxy using the configured Axios API client
          const response = await API.get('/upload/proxy', {
            params: { url: file.viewLink },
            responseType: 'arraybuffer'
          });
          uint8Array = new Uint8Array(response.data);
        }

        // Configure PDF.js Worker
        pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

        const pdf = await pdfjsLib.getDocument({ data: uint8Array }).promise;
        setPdfDoc(pdf);
        setLoading(false);
      } catch (err) {
        console.error('PDF.js loading error:', err);
        // Fallback to Google Docs Viewer if local PDF.js fails
        setFallback(true);
        setLoading(false);
      }
    };

    loadPdf();
  }, [file]);

  if (loading) {
    return (
      <div className="pdf-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '30px 10px', gap: '10px' }}>
        <div className="spinner" style={{ width: '24px', height: '24px', border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#16a34a', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Loading PDF preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdf-error" style={{ color: '#ef4444', padding: '20px', fontSize: '13px', textAlign: 'center' }}>
        ⚠️ {error}
      </div>
    );
  }

  if (fallback) {
    const src = `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(file.viewLink)}`;
    return (
      <div className="pdf-iframe-wrap" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
        <iframe
          src={src}
          style={{ width: '100%', flex: 1, border: 'none', minHeight: '320px' }}
          title={file.name || 'PDF Preview'}
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
        {file.viewLink && (
          <div style={{ textAlign: 'center', padding: '8px', background: '#111120' }}>
            <a 
              href={file.viewLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ color: 'var(--primary-light, #6C63FF)', fontSize: '12px', textDecoration: 'none', fontWeight: '600' }}
            >
              ↗ View original document in new tab
            </a>
          </div>
        )}
      </div>
    );
  }

  const numPages = pdfDoc ? pdfDoc.numPages : 0;
  const pagesArray = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <div
      ref={containerRef}
      className="pdf-preview-container"
      style={{
        overflowY: 'auto',
        height: '100%',
        width: '100%',
        padding: '16px 8px',
        boxSizing: 'border-box',
        background: '#111120'
      }}
    >
      {pagesArray.map((pageNum) => {
        const isSelected = pageSelection === 'all' || selectedPages.includes(pageNum);
        return (
          <PDFPage
            key={pageNum}
            pdfDoc={pdfDoc}
            pageNum={pageNum}
            totalPages={numPages}
            isSelected={isSelected}
            pageSelection={pageSelection}
            onClick={() => {
              if (pageSelection === 'custom') {
                onPageToggle?.(pageNum);
              }
            }}
            orientation={orientation}
            scale={scale}
            containerWidth={containerWidth}
          />
        );
      })}
    </div>
  );
};

export default PDFViewer;
